import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AssistantChatInstructions,
  AssistantChatInstructionsFailed,
  AssistantChatInstructionsSaved,
} from '../../interfaces/assistant-chat.interface';
import { AssistantChatInstructionsService } from '../../services/assistant-chat/assistant-chat.instructions.service';

const NOTE_SECONDS = 3;

@Component({
  selector: 'assistant-chat-instructions',
  templateUrl: './assistant-chat-instructions.component.html',
  imports: [FormsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AssistantChatInstructionsComponent implements OnInit, OnDestroy {
  @Output() saved = new EventEmitter<AssistantChatInstructionsSaved>();
  @Output() failed = new EventEmitter<AssistantChatInstructionsFailed>();

  public instructions?: AssistantChatInstructions;
  public draft: string = '';
  public note: string = '';
  public savedVisible: boolean = false;
  public saving: boolean = false;

  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly instructionsService = inject(AssistantChatInstructionsService);
  private savedTimer?: ReturnType<typeof setTimeout>;

  public get changed(): boolean {
    return !!this.instructions && this.draft.trim() !== this.instructions.text;
  }

  public ngOnInit(): void {
    this.instructionsService
      .load()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: instructions => this.show(instructions),
        error: () => {
          this.note = 'Could not load the instructions.';
          this.failed.emit({ step: 'load' });
          this.changeDetectorRef.markForCheck();
        },
      });
  }

  public ngOnDestroy(): void {
    clearTimeout(this.savedTimer);
  }

  public save(): void {
    this.saving = true;
    this.note = '';
    this.instructionsService
      .save(this.draft.trim())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: instructions => {
          this.show(instructions);
          this.flashSaved();
          this.saved.emit({ cleared: !instructions.text, length: instructions.text.length });
        },
        error: () => {
          this.saving = false;
          this.note = 'Could not save. Try again.';
          this.failed.emit({ step: 'save' });
          this.changeDetectorRef.markForCheck();
        },
      });
  }

  public onInput(): void {
    this.savedVisible = false;
    this.note = '';
  }

  private flashSaved(): void {
    clearTimeout(this.savedTimer);
    this.savedVisible = true;
    this.changeDetectorRef.markForCheck();
    this.savedTimer = setTimeout(() => {
      this.savedVisible = false;
      this.changeDetectorRef.markForCheck();
    }, NOTE_SECONDS * 1000);
  }

  private show(instructions: AssistantChatInstructions): void {
    this.instructions = instructions;
    this.draft = instructions.text;
    this.saving = false;
    this.changeDetectorRef.markForCheck();
  }
}
